import { useAct } from 'FlowBuilder/store'
import FileUploader from 'Shared/components/ImageUploader/FileUploader'
import imageUrlFromSignedBlobId from 'Shared/functions/imageUrlFromSignedBlobId'
import './ShareSettingsForm.scoped.scss'
import useGenericTranslate from 'FlowBuilder/hooks/useGenericTranslateFlow'
import { channelConfig } from 'FlowBuilder/components/ActionViews/Share/shareTypes'

const CHANNEL_ICON_SIZE = 24

function isAllChannels(channels) {
  return !channels || channels.length === 0 || (channels.length === 1 && channels[0] === 'all')
}

export default function ShareSettingsForm() {
  const act = useAct()
  const [fetchingMetatags, setFetchingMetatags] = useState(false)
  const [fetchError, setFetchError] = useState(null)

  const shareUrl = useSel(s => s.builder?.flow?.shareUrl || '')

  const contentVariants = useSel(s =>
    (s.builder?.flow?.shareContentTest?.contentVariants || [])
      .filter(cv => cv.active !== false)
      .sort((a, b) => (a.content?.index ?? 0) - (b.content?.index ?? 0))
  )

  const shareContentVariantsEnabled = window.ENV.FEATURES['share_content_variants']

  function addVariant() {
    act.builder.addFlowContentTestVariant('shareContentTest')
  }

  function onShareUrlChange(e) {
    setFetchError(null)
    act.builder.updateFlow('shareUrl', e.target.value)
  }

  function fetchMetatags() {
    setFetchError(null)
    setFetchingMetatags(true)
    act.builder.fetchMetatags()
      .catch((err) => {
        const message = err?.response?.data?.error || 'Failed to fetch metatags from the URL'
        setFetchError(message)
      })
      .finally(() => setFetchingMetatags(false))
  }

  return <div className="share-settings">
    <div className="share-settings-header">
      <h2>Share Settings</h2>
      <p className="share-settings-subtitle">Configure share metadata for social platforms.</p>
    </div>

    <div className="share-url-section">
      <div className="field">
        <label>Share a different page (optional)</label>
        <p class="dark-grey smaller bold">
          When set, shared links will redirect to this URL instead of this page. HTTPS will be used automatically.
        </p>
        <div className="share-url-input-row">
          <input
            type="url"
            value={shareUrl}
            placeholder="example.com/campaign"
            onChange={onShareUrlChange}
          />
          {contentVariants.length === 1 && <button
            className="fetch-metatags-btn"
            onClick={fetchMetatags}
            disabled={fetchingMetatags || !shareUrl.trim()}
            type="button"
          >
            {fetchingMetatags ? 'Fetching…' : 'Pre-fill from URL metatags'}
          </button>}
        </div>
        {fetchError && <p className="fetch-error">{fetchError}</p>}
      </div>
    </div>

    {contentVariants.map((variant, index) => {
      function settingUpdater({ key, language }) {
        return (value) => {
          act.builder.updateFlowContentTest({ slug: 'shareContentTest', contentVariantId: variant.id, key, value, language })
        }
      }

      function onSetChannels(channels) {
        act.builder.updateFlowContentTestChannels({ slug: 'shareContentTest', contentVariantId: variant.id, channels })
      }

      return <Variant
        key={variant.id}
        versionNumber={index + 1}
        textContent={variant.content.textContent}
        channels={variant.content.channels || ['all']}
        onChange={settingUpdater}
        onSetChannels={onSetChannels}
        canDelete={contentVariants.length > 1}
        showVariantActions={shareContentVariantsEnabled}
        onDuplicate={() => act.builder.duplicateFlowContentTestVariant('shareContentTest', variant.id)}
        onDelete={() => act.builder.deactivateFlowContentTestVariant('shareContentTest', variant.id)}
      />
    })}

    {shareContentVariantsEnabled && <button className="add-version-button" onClick={addVariant} type="button">
      + Add Version
    </button>}
  </div>
}

function ChannelSelector({ channels, onSetChannels }) {
  const [editing, setEditing] = useState(false)
  const allChannels = isAllChannels(channels)

  function onToggleChannel(channelKey) {
    const currentChannels = allChannels ? [] : [...channels]
    const newChannels = currentChannels.includes(channelKey)
      ? currentChannels.filter(c => c !== channelKey)
      : [...currentChannels, channelKey]
    onSetChannels(newChannels.length === 0 ? ['all'] : newChannels)
  }

  function restoreAllChannels() {
    onSetChannels(['all'])
    setEditing(false)
  }

  if (!editing && allChannels) {
    return <div className="channel-selector-field">
      <span className="channel-all-label">This version will show on all channels</span>
      <button className="channel-edit-link" onClick={() => setEditing(true)} type="button">Restrict to specific channels</button>
    </div>
  }

  return <div className="channel-selector-field">
    <label>Show this version on these channels</label>
    <div className="channel-badges-wrapper">
      <div className="channel-badges">
        {Object.entries(channelConfig).map(([key, config]) => {
          const isActive = allChannels || channels.includes(key)
          const Icon = config.svg.type
          return <button
            key={key}
            className={`channel-badge ${isActive ? 'active' : ''}`}
            onClick={() => {
              if (allChannels) {
                onSetChannels([key])
              } else {
                onToggleChannel(key)
              }
            }}
            type="button"
          >
            <span className="channel-check">{isActive ? '\u2713' : ''}</span>
            <span className="channel-icon"><Icon width={CHANNEL_ICON_SIZE} height={CHANNEL_ICON_SIZE} /></span>
            <span className="channel-label">{config.title}</span>
          </button>
        })}
      </div>
      <button className="channel-restore-link" onClick={restoreAllChannels} type="button">Show on all channels</button>
    </div>
  </div>
}

function Variant({ versionNumber, textContent, channels, onChange, onSetChannels, canDelete, showVariantActions, onDuplicate, onDelete }) {
  const [uploadedFile, setUploadedFile] = useState(null)

  const {
    translated: { shareTitle, shareDescription, shareImageUrl, shareReason },
    fallback: { shareTitle: shareTitleFallback, shareDescription: shareDescriptionFallback, shareImageUrl: shareImageUrlFallback, shareReason: shareReasonFallback },
    language,
    flag
  } = useGenericTranslate({ textContent })

  const handleImageUploaded = (blobIds, filename) => {
    if (blobIds && blobIds.length > 0) {
      const imageUrl = imageUrlFromSignedBlobId(blobIds[0])
      onChange({ key: 'shareImageUrl', language })(imageUrl)
      setUploadedFile(null)
    }
  }

  function getPreviewImageSrc() {
    if (uploadedFile) {
      return URL.createObjectURL(uploadedFile)
    }
    return shareImageUrl || shareImageUrlFallback
  }

  const hasPreviewImage = uploadedFile || shareImageUrl || shareImageUrlFallback

  return <div className="version-card">
    {showVariantActions && <div className="version-header">
      <span className="version-number">Version {versionNumber}</span>
      <div className="version-actions">
        <button className="version-action-btn" onClick={onDuplicate} title="Duplicate version" type="button">
          <SvgIconsCopy />
        </button>
        {canDelete && <button className="version-action-btn delete-btn" onClick={onDelete} title="Delete version" type="button">
          <SvgIconsTrash />
        </button>}
      </div>
    </div>}

    {showVariantActions && <ChannelSelector channels={channels} onSetChannels={onSetChannels} />}

    <div className="version-content">
      <div className="version-form">
        <div className="field">
          <label>Share title {flag}</label>
          <input
            type="text"
            value={shareTitle ?? ''}
            placeholder={shareTitleFallback ?? ''}
            onChange={e => onChange({ key: 'shareTitle', language })(e.target.value)}
          />
        </div>

        <div className="field">
          <label>Share description {flag}</label>
          <textarea
            rows={3}
            value={shareDescription ?? ''}
            placeholder={shareDescriptionFallback ?? ''}
            onChange={e => onChange({ key: 'shareDescription', language })(e.target.value)}
          />
        </div>

        <div className="field">
          <label>Prewritten share text {flag}</label>
          <textarea
            rows={2}
            value={shareReason ?? ''}
            placeholder={shareReasonFallback ?? ''}
            onChange={e => onChange({ key: 'shareReason', language })(e.target.value)}
          />
          <p className="field-hint">Only applies to Twitter/X and WhatsApp shares</p>
        </div>

        <div className="field">
          <label>Share image URL {flag}</label>
          <input
            type="text"
            value={shareImageUrl ?? ''}
            placeholder={shareImageUrlFallback ?? ''}
            onChange={e => onChange({ key: 'shareImageUrl', language })(e.target.value)}
          />
          <div className="upload-option">
            <FileUploader onUploaded={handleImageUploaded} inline={true} />
          </div>
        </div>
      </div>

      <div className="version-preview">
        <h3 className="preview-title">Share preview</h3>
        <div className="wa-preview">
          {(shareReason || shareReasonFallback) && <div className="wa-bubble">
            <p className="wa-share-text">{shareReason || shareReasonFallback}</p>
          </div>}
          <div className="wa-bubble">
            <div className="wa-link-card">
              {hasPreviewImage && <img src={getPreviewImageSrc()} alt="Share preview" />}
              <div className="wa-link-info">
                <span className="wa-domain">yoursite.com</span>
                <strong className="wa-link-title">{shareTitle || shareTitleFallback || 'Share Title'}</strong>
                <p className="wa-link-description">{shareDescription || shareDescriptionFallback || 'Share description'}</p>
              </div>
            </div>
            <span className="wa-time">12:34</span>
          </div>
        </div>
      </div>
    </div>
  </div>
}
